import { useState, forwardRef, useImperativeHandle, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { QUOTE } from "@/constants/testIds";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PACKAGES_FOR_SELECT = [
  "Petite Play",
  "Signature Play",
  "Royal Bounce Experience",
  "Not sure yet",
];

// Local date as YYYY-MM-DD so the min date matches the visitor's timezone.
const toISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const todayStr = () => toISODate(new Date());

const QuoteForm = forwardRef(function QuoteForm(_, ref) {
  const sectionRef = useRef(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    event_date: "",
    package: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  useImperativeHandle(ref, () => ({
    scrollIntoView: (preselectPackage) => {
      if (preselectPackage) {
        setForm((f) => ({ ...f, package: preselectPackage }));
      }
      sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    },
  }));

  const update = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");
    // Defensive guard in case the browser ignores the min attribute.
    if (form.event_date && form.event_date < todayStr()) {
      const msg = "Please pick today or a future date for your event.";
      setStatus("error");
      setErrorMsg(msg);
      toast.error("Couldn't send your request", { description: msg });
      return;
    }
    try {
      await axios.post(`${API}/leads`, form);
      setStatus("success");
      toast.success("Thanks! Your request was sent.", {
        description: "We'll be in touch within 24 hours.",
      });
      setForm({
        name: "",
        email: "",
        phone: "",
        event_date: "",
        package: "",
        message: "",
      });
    } catch (err) {
      setStatus("error");
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Something went wrong. Please try again.";
      setErrorMsg(typeof detail === "string" ? detail : "Something went wrong.");
      toast.error("Couldn't send your request", { description: String(detail) });
    }
  };

  return (
    <section
      id="quote"
      ref={sectionRef}
      data-testid={QUOTE.section}
      className="relative py-24 md:py-32 bg-brand-cream/70 overflow-hidden"
    >
      <div className="pointer-events-none absolute -top-24 right-0 w-[420px] h-[420px] rounded-full bg-brand-sage/50 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 -left-24 w-[360px] h-[360px] rounded-full bg-brand-blush/50 blur-3xl" />

      <div className="relative max-w-6xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-brand-terracotta mb-5">
            Request a quote
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-[58px] font-light leading-[1.05] text-brand-ink text-balance">
            Dates are limited.
            <br />
            <span className="italic text-brand-terracotta">Secure the fun</span> for your event today.
          </h2>
          <p className="mt-6 text-lg text-brand-mute font-light max-w-md">
            Tell us a little about your event and we&apos;ll send a personalized
            quote within 24 hours.
          </p>

          <div className="mt-10 space-y-4">
            <div className="flex items-center gap-3 text-sm text-brand-mute">
              <span className="h-2 w-2 rounded-full bg-brand-sage" />
              Free delivery within Syracuse, NY
            </div>
            <div className="flex items-center gap-3 text-sm text-brand-mute">
              <span className="h-2 w-2 rounded-full bg-brand-gold" />
              Fully sanitized before every event
            </div>
            <div className="flex items-center gap-3 text-sm text-brand-mute">
              <span className="h-2 w-2 rounded-full bg-brand-blush" />
              Premium, photo-ready neutral aesthetic
            </div>
          </div>
        </div>

        <form
          data-testid={QUOTE.form}
          onSubmit={onSubmit}
          className="lg:col-span-7 bg-white rounded-3xl border border-[#E9DED1] p-7 md:p-10 shadow-xl shadow-[#B8A792]/15"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Your name">
              <input
                data-testid={QUOTE.name}
                required
                value={form.name}
                onChange={update("name")}
                placeholder="Jane Doe"
                className={inputCls}
              />
            </Field>
            <Field label="Email">
              <input
                data-testid={QUOTE.email}
                required
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="jane@email.com"
                className={inputCls}
              />
            </Field>
            <Field label="Phone">
              <input
                data-testid={QUOTE.phone}
                required
                value={form.phone}
                onChange={update("phone")}
                placeholder="(315) 555-0123"
                className={inputCls}
              />
            </Field>
            <Field label="Event date">
              <input
                data-testid={QUOTE.date}
                type="date"
                min={todayStr()}
                value={form.event_date}
                onChange={update("event_date")}
                className={inputCls}
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Package">
                <Select value={form.package} onValueChange={(v) => setForm((f) => ({ ...f, package: v }))}>
                  <SelectTrigger
                    data-testid={QUOTE.package}
                    className="bg-white border border-[#E9DED1] focus:ring-2 focus:ring-brand-sage focus:border-transparent rounded-xl h-12 px-4 text-brand-ink"
                  >
                    <SelectValue placeholder="Pick a package (or 'Not sure yet')" />
                  </SelectTrigger>
                  <SelectContent>
                    {PACKAGES_FOR_SELECT.map((p) => (
                      <SelectItem key={p} value={p}>
                        {p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Tell us about your event">
                <textarea
                  data-testid={QUOTE.message}
                  rows={4}
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Birthday for our 2-year-old, ~25 guests, indoor venue..."
                  className={`${inputCls} resize-none`}
                />
              </Field>
            </div>
          </div>

          {status === "error" && (
            <p data-testid={QUOTE.error} className="mt-5 text-sm text-red-700">
              {errorMsg}
            </p>
          )}
          {status === "success" && (
            <p data-testid={QUOTE.success} className="mt-5 text-sm text-brand-ink bg-brand-sage/40 rounded-lg px-4 py-3">
              Thanks! We received your request and will reply within 24 hours.
            </p>
          )}

          <div className="mt-7 flex items-center justify-between gap-4 flex-wrap">
            <p className="text-xs text-brand-taupe">
              No spam. Your info is only used to send your quote.
            </p>
            <button
              type="submit"
              data-testid={QUOTE.submit}
              disabled={status === "loading"}
              className="inline-flex items-center gap-2 glass-btn-solid disabled:opacity-60 rounded-full px-8 py-4 font-medium transition-all duration-300"
            >
              {status === "loading" ? "Sending..." : "Send my request"}
              {status !== "loading" && <span aria-hidden>→</span>}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
});

const inputCls =
  "w-full bg-white border border-[#E9DED1] focus:ring-2 focus:ring-brand-sage focus:border-transparent rounded-xl h-12 px-4 text-brand-ink placeholder:text-brand-taupe outline-none transition";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-[0.18em] text-brand-mute mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}

export default QuoteForm;
